import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { Check, ChevronDown, ChevronUp, Code, Copy, Eye, FileText } from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { Button } from "@/components/ui/button"
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { CodeBlock } from "@/features/chat/components/code-block"
import { cn } from "@/lib/utils"

type NodeOutputPopoverProps = {
  output: string
  children: React.ReactNode
  disabled?: boolean
}

const PREVIEW_LENGTH = 300
const NODE_IO_SCHEMA = "workflow_node_io/v1"

type NodeIoSection = {
  raw?: string
  format?: string
  truncated?: boolean
  original_length?: number
}

type NodeIoPayload = {
  schema?: string
  meta?: {
    node_id?: string
    node_type?: string
    label?: string
    // Loop-specific metadata
    iterations?: number
    mode?: string
    resultMode?: string
  }
  input?: NodeIoSection
  output?: NodeIoSection
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function parseJson(input: string): unknown | null {
  try {
    return JSON.parse(input) as unknown
  } catch {
    return null
  }
}

function getNodeIoPayload(value: unknown): NodeIoPayload | null {
  if (!isRecord(value)) return null
  if (value.schema !== NODE_IO_SCHEMA) return null
  return value as NodeIoPayload
}

function normalizeLanguage(format: string | undefined): string {
  const normalized = (format || "").toString().trim().toLowerCase()
  if (normalized === "json") return "json"
  if (normalized === "markdown" || normalized === "md") return "markdown"
  return "text"
}

function buildPreviewText(text: string, isExpanded: boolean) {
  const isLong = text.length > PREVIEW_LENGTH
  const displayText = isExpanded ? text : text.slice(0, PREVIEW_LENGTH)
  const isPreviewTruncated = !isExpanded && isLong
  return { displayText, isLong, isPreviewTruncated }
}

type SectionContentProps = {
  rawText: string
  displayText: string
  viewMode: "rendered" | "raw"
  sectionLanguage: string
  isPreviewTruncated: boolean
}

function SectionContent({
  rawText,
  displayText,
  viewMode,
  sectionLanguage,
  isPreviewTruncated,
}: SectionContentProps) {
  const { t } = useTranslation()

  if (rawText.length === 0) {
    return (
      <div className="text-muted-foreground py-8 text-center text-sm">
        {t("workflowEditor.nodeOutput.empty", "No data")}
      </div>
    )
  }

  if (viewMode === "raw" || sectionLanguage === "json") {
    const languageClass = viewMode === "raw" ? `language-${sectionLanguage}` : "language-json"
    return (
      <div className="[&>section]:my-0">
        <CodeBlock className={languageClass}>{displayText}</CodeBlock>
        {isPreviewTruncated && <div className="text-muted-foreground mt-2 text-[11px]">…</div>}
      </div>
    )
  }

  return (
    <div className="min-w-0">
      <MarkdownRenderer
        className="prose-sm max-w-full text-sm [&>*]:max-w-full"
        content={displayText + (isPreviewTruncated ? "..." : "")}
      />
    </div>
  )
}

const RESULT_MODE_LABELS: Record<string, string> = {
  collect: "Collect all",
  join: "Join",
  last: "Last only",
  sum: "Sum",
}

const MODE_LABELS: Record<string, string> = {
  count: "Count",
  array: "Array",
}

type LoopMetadataBadgesProps = {
  iterations?: number
  mode?: string
  resultMode?: string
}

function LoopMetadataBadges({ iterations, mode, resultMode }: LoopMetadataBadgesProps) {
  if (!iterations && !mode && !resultMode) return null

  return (
    <div className="bg-muted/50 flex flex-wrap items-center gap-1.5 border-b px-3 py-2">
      {iterations !== undefined && (
        <span className="bg-chart-1 border-foreground text-chart-foreground inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium">
          {iterations} {iterations === 1 ? "iteration" : "iterations"}
        </span>
      )}
      {mode && (
        <span className="bg-muted text-muted-foreground inline-flex items-center rounded-md border border-transparent px-2 py-0.5 text-[11px] font-medium">
          {MODE_LABELS[mode] ?? mode}
        </span>
      )}
      {resultMode && (
        <span className="bg-success/15 border-success text-foreground inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium">
          {RESULT_MODE_LABELS[resultMode] ?? resultMode}
        </span>
      )}
    </div>
  )
}

export function NodeOutputPopover({ output, children, disabled }: NodeOutputPopoverProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const [activeTab, setActiveTab] = useState<"output" | "input">("output")
  const [viewMode, setViewMode] = useState<"rendered" | "raw">("rendered")

  const parsed = useMemo(() => parseJson(output), [output])
  const nodeIoPayload = useMemo(() => getNodeIoPayload(parsed), [parsed])
  const jsonString = useMemo(() => {
    if (nodeIoPayload) return null
    if (parsed === null) return null
    try {
      return JSON.stringify(parsed, null, 2)
    } catch {
      return null
    }
  }, [nodeIoPayload, parsed])

  const activeSection = useMemo(() => {
    if (!nodeIoPayload) return null
    return activeTab === "input" ? nodeIoPayload.input : nodeIoPayload.output
  }, [nodeIoPayload, activeTab])

  const rawSectionText = typeof activeSection?.raw === "string" ? activeSection.raw : ""
  const sectionFormat = typeof activeSection?.format === "string" ? activeSection.format : undefined
  const sectionLanguage = normalizeLanguage(sectionFormat)
  const isBackendTruncated = !!activeSection?.truncated
  const originalLength =
    typeof activeSection?.original_length === "number" ? activeSection.original_length : undefined

  const renderedSectionText = useMemo(() => {
    if (!nodeIoPayload) return null
    if (viewMode !== "rendered") return null
    if (sectionLanguage !== "json") return rawSectionText
    const parsedSection = parseJson(rawSectionText)
    if (parsedSection === null) return rawSectionText
    try {
      return JSON.stringify(parsedSection, null, 2)
    } catch {
      return rawSectionText
    }
  }, [nodeIoPayload, rawSectionText, sectionLanguage, viewMode])

  const contentForPreview = useMemo(() => {
    if (!nodeIoPayload) return jsonString ?? output
    return viewMode === "raw" ? rawSectionText : (renderedSectionText ?? "")
  }, [nodeIoPayload, viewMode, rawSectionText, renderedSectionText, jsonString, output])

  const { displayText, isLong, isPreviewTruncated } = buildPreviewText(
    contentForPreview,
    isExpanded
  )

  if (disabled || !output) {
    return <>{children}</>
  }

  const handleCopy = async () => {
    const textToCopy = nodeIoPayload ? rawSectionText : output
    await navigator.clipboard.writeText(textToCopy)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const headerTitle = (() => {
    const label = nodeIoPayload?.meta?.label
    return label && label.trim().length > 0
      ? label
      : t("workflowEditor.nodeOutput.title", "Node Output")
  })()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/*
        Anchor (not Trigger) wraps the whole node card so the popover stays
        positioned relative to it without turning every inline control
        (inputs, pickers, checkboxes) into a popover toggle. A dedicated
        affordance below is the only thing that opens the output view.
      */}
      <PopoverAnchor asChild>
        <div className="relative">
          {children}
          {/*
            Dedicated trigger overlay placed over the node's bottom-right
            corner — the same spot where NodeExecutionIndicator renders its
            green checkmark / "View output" pill (see node-execution-indicator.tsx).
            It's transparent so the existing badge stays the visible affordance,
            but it captures the click so only this corner opens the popover.
            Inline editing controls inside `children` no longer act as triggers.
          */}
          <PopoverTrigger asChild>
            <button
              aria-expanded={open}
              aria-label={t("workflowEditor.nodeOutput.viewOutput", "View output")}
              className="nodrag absolute -right-2 -bottom-2 z-20 h-8 w-12 cursor-pointer rounded-full bg-transparent"
              data-testid="node-output-trigger"
              title={t("workflowEditor.nodeOutput.viewOutput", "View output")}
              type="button"
            />
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className={cn("w-96 p-0", isExpanded && "w-[32rem]")}
        side="right"
        sideOffset={8}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="bg-muted/30 flex items-center justify-between border-b px-3 py-2">
          <div className="flex items-center gap-2">
            <FileText className="text-muted-foreground size-4" />
            <div className="min-w-0">
              <div className="truncate text-xs font-medium">{headerTitle}</div>
              {nodeIoPayload?.meta?.node_type && (
                <div className="text-muted-foreground truncate text-[10px]">
                  {nodeIoPayload.meta.node_type}
                </div>
              )}
            </div>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button className="size-7" size="icon" variant="ghost" onClick={handleCopy}>
                  {copied ? (
                    <Check className="text-success size-3.5" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t("workflowEditor.nodeOutput.copyOutput", "Copy output")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Loop metadata badges for loopContainer nodes */}
        {nodeIoPayload?.meta?.node_type === "loopContainer" && (
          <LoopMetadataBadges
            iterations={nodeIoPayload.meta.iterations}
            mode={nodeIoPayload.meta.mode}
            resultMode={nodeIoPayload.meta.resultMode}
          />
        )}

        {nodeIoPayload ? (
          <>
            <div className="bg-muted/20 flex items-center justify-between gap-2 border-b px-3 py-2">
              <Tabs
                value={activeTab}
                onValueChange={(value) => setActiveTab(value as "output" | "input")}
              >
                <TabsList className="h-7">
                  <TabsTrigger className="h-6 px-2 text-xs" value="output">
                    {t("workflowEditor.nodeOutput.output", "Output")}
                  </TabsTrigger>
                  <TabsTrigger className="h-6 px-2 text-xs" value="input">
                    {t("workflowEditor.nodeOutput.input", "Input")}
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <Tabs
                value={viewMode}
                onValueChange={(value) => setViewMode(value as "rendered" | "raw")}
              >
                <TabsList className="h-7">
                  <TabsTrigger
                    className="h-6 px-2"
                    title={t("workflowEditor.nodeOutput.rendered", "Rendered")}
                    value="rendered"
                  >
                    <Eye className="size-3.5" />
                  </TabsTrigger>
                  <TabsTrigger
                    className="h-6 px-2"
                    title={t("workflowEditor.nodeOutput.raw", "Raw")}
                    value="raw"
                  >
                    <Code className="size-3.5" />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className={cn("overflow-y-auto p-3", isExpanded ? "max-h-[70vh]" : "max-h-56")}>
              {(isBackendTruncated || originalLength !== undefined) && (
                <div className="text-muted-foreground mb-2 text-[11px]">
                  {isBackendTruncated
                    ? t("workflowEditor.nodeOutput.truncated", "Truncated")
                    : t("workflowEditor.nodeOutput.size", "Size")}
                  {originalLength !== undefined && ` • ${originalLength.toLocaleString()} chars`}
                </div>
              )}

              <SectionContent
                displayText={displayText}
                isPreviewTruncated={isPreviewTruncated}
                rawText={rawSectionText}
                sectionLanguage={sectionLanguage}
                viewMode={viewMode}
              />
            </div>
          </>
        ) : (
          <div className={cn("overflow-y-auto p-3", isExpanded ? "max-h-[70vh]" : "max-h-48")}>
            {jsonString ? (
              <div className="overflow-x-auto">
                <pre className="text-foreground font-mono text-xs whitespace-pre">
                  {displayText}
                  {isPreviewTruncated ? "..." : ""}
                </pre>
              </div>
            ) : (
              <div className="min-w-0">
                <MarkdownRenderer
                  className="prose-sm max-w-full text-sm [&>*]:max-w-full"
                  content={displayText + (isPreviewTruncated ? "..." : "")}
                />
              </div>
            )}
          </div>
        )}

        {isLong && (
          <div className="border-t px-3 py-2">
            <Button
              className="h-7 w-full gap-1.5 text-xs"
              size="sm"
              variant="ghost"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="size-3.5" />
                  {t("workflowEditor.nodeOutput.showLess", "Show less")}
                </>
              ) : (
                <>
                  <ChevronDown className="size-3.5" />
                  {t("workflowEditor.nodeOutput.showMore", "Show full output")}
                </>
              )}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
